import getLang from 'I18n/utils/getLang';
import enviro from 'enviro';
import getUserInfo from 'hub-http/userInfo';
import Raven from 'raven-js';
import { getIdentificationToken } from 'purchase-motions-api/query/helpdesk';
import { getTrackingProperties, track } from './usageTracker';
const openSalesChat = ({
  apiName,
  hostWindow,
  trackingData,
  upgradeConfig
}) => {
  if (!hostWindow.HubSpotConversations) {
    Raven.captureException(new Error('Attempted to open chat without the widget being loaded'), {
      tags: {
        location: hostWindow.location.href
      },
      fingerprint: ['sales-chat-widget-not-loaded']
    });
    return;
  }
  if (hostWindow.hubspot && hostWindow.hubspot.zorse && !hostWindow.hubspot.zorse.supressed) {
    hostWindow.hubspot.zorse.minimizeZorseWidgetButton();
  }
  hostWindow.HubSpotConversations.on('conversationStarted', __payload => {
    track('communication-methods', Object.assign({
      action: 'started conversation'
    }, getTrackingProperties({
      apiName,
      trackingData,
      upgradeConfig,
      sourceKey: 'TALK_TO_SALES'
    })));
  });
  hostWindow.HubSpotConversations.on('conversationClosed', __payload => {
    track('communication-methods', {
      action: 'ended conversation'
    });
    hostWindow.HubSpotConversations.widget.close();
    if (hostWindow.hubspot && hostWindow.hubspot.zorse && !hostWindow.hubspot.zorse.supressed) {
      hostWindow.hubspot.zorse.restoreZorseWidgetButton();
    }
  });
  const status = hostWindow.HubSpotConversations.widget.status();
  const loadWidget = widgetOpen => {
    return new Promise(resolve => {
      hostWindow.HubSpotConversations.widget.load({
        widgetOpen
      });
      setTimeout(resolve, 500);
    });
  };
  if (status.loaded) {
    hostWindow.HubSpotConversations.widget.remove();
    loadWidget(true).then(() => {
      hostWindow.HubSpotConversations.widget.open();
    }).catch(error => {
      Raven.captureException(new Error(`Failed to open chat after reloading: ${error instanceof Error ? error.message : String(error)}`, {
        cause: error
      }), {
        fingerprint: ['sales-chat-reload-failed']
      });
    });
  } else {
    loadWidget(true).then(() => {
      hostWindow.HubSpotConversations.widget.open();
    }).catch(error => {
      Raven.captureException(new Error(`Failed to open chat: ${error instanceof Error ? error.message : String(error)}`, {
        cause: error
      }), {
        fingerprint: ['sales-chat-load-failed']
      });
    });
  }
};
const mountConversationsScriptTag = hostWindow => {
  const chatPortal = enviro.isProd() ? '53' : '100048175';
  const script = hostWindow.document.createElement('script');
  const env = enviro.getShort();
  const hublet = enviro.getHublet();
  script.type = 'text/javascript';
  script.id = 'hubspot-messages-loader';
  script.async = false;
  script.defer = false;
  script.src = `//js.usemessages${enviro.isProd() ? '' : 'qa'}.com/conversations-embed.js`;
  script.setAttribute('data-hsjs-portal', chatPortal);
  script.setAttribute('data-hsjs-env', env);
  script.setAttribute('data-hs-js-hublet', hublet);
  hostWindow.document.body.appendChild(script);
};
const addChatQueryParams = (hostWindow, isRetail, isAssignable) => {
  const getQueryParam = () => {
    if (isRetail) {
      return `show-retail-tts-chat-${getLang()}`;
    } else if (isAssignable) {
      return `show-assignable-tts-chat-${getLang()}`;
    } else {
      return `show-unassignable-tts-chat-${getLang()}`;
    }
  };
  const queryParam = `${hostWindow.location.search.length ? '&' : '?'}${getQueryParam()}=true`;
  const queryParamIsPresent = hostWindow.location.search.includes(queryParam.slice(1));
  if (!queryParamIsPresent) {
    hostWindow.history.pushState(null, '', `${hostWindow.location.href}${queryParam}`);
  }
};
const initializeChatSettings = (hostWindow, isRetail, isAssignable) => {
  addChatQueryParams(hostWindow, isRetail, isAssignable);
  return Promise.all([getUserInfo(), getIdentificationToken()]).then(([userInfo, tokenObject]) => {
    const token = tokenObject.token;
    if (!hostWindow.hsConversationsSettings) {
      hostWindow.hsConversationsSettings = {};
    }
    hostWindow.hsConversationsSettings.loadImmediately = false;
    hostWindow.hsConversationsSettings.identificationEmail = userInfo.user.email;
    hostWindow.hsConversationsSettings.identificationToken = token;
  }).catch(error => {
    Raven.captureException(error);
    if (!hostWindow.hsConversationsSettings) {
      hostWindow.hsConversationsSettings = {};
    }
    hostWindow.hsConversationsSettings.loadImmediately = true;
  });
};

/**
 * Adds the required query parameter, loads the Conversations script tag,
 * and opens the chat when it's ready.
 *
 *
 * Note that `show-tts-chat=true` is a specific query parameter for
 * the [chatflow in 53](https://app.hubspot.com/chatflows/53/edit/live-chat/548260/target).
 */

export const loadAndOpenSalesChatInWindow = hostWindow => ({
  apiName,
  trackingData,
  isRetail = false,
  isAssignable = false
}) => {
  if (hostWindow) {
    let windowToUse = hostWindow;
    // When in an iframe, the hostWindow is the parent
    if (hostWindow.self !== hostWindow.top) {
      windowToUse = hostWindow.parent;
    }
    initializeChatSettings(windowToUse, isRetail, isAssignable).then(() => {
      if (windowToUse.HubSpotConversations) {
        openSalesChat({
          apiName,
          hostWindow: windowToUse,
          trackingData
        });
      } else {
        if (!Array.isArray(windowToUse.hsConversationsOnReady)) {
          windowToUse.hsConversationsOnReady = [];
        }
        const openChatCallback = () => {
          try {
            openSalesChat({
              apiName,
              hostWindow: windowToUse,
              trackingData
            });
          } catch (error) {
            Raven.captureException(new Error(`Failed to open chat: ${error instanceof Error ? error.message : String(error)}`), {
              fingerprint: ['sales-chat-callback-error']
            });
          }
        };
        windowToUse.hsConversationsOnReady.push(openChatCallback);
        mountConversationsScriptTag(windowToUse);
      }
    }).catch(error => {
      Raven.captureException(error, {
        fingerprint: ['sales-chat-settings-error']
      });
    });
  }
};
export const loadAndOpenSalesChat = params => {
  const hubspotWindow = window;
  loadAndOpenSalesChatInWindow(hubspotWindow)(params);
};